import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { Status } from '../../models/common/Status';
import { RootState } from '../store';
import { TaskBase, TaskQuery } from '../../models/Task/Task';
import { taskApi } from '../../services/taskApi';

export interface OutgoingCallsState {
  items: TaskBase[];
  totalItemsCount: number;
  status: Status;
}

export interface OutgoingCallsResponse {
  items: TaskBase[];
  totalItemsCount: number;
}

export interface OutgoingCallsRequest {
  'Filter.From': string;
  'Filter.To': string;
  PageIndex: number;
  PageSize: number;
  SortKeySelector: string;
  'Filter.ClinicIds': string[];
  'Filter.SpecializationIds': string[];
  'Filter.DoctorIds': string[];
  'Filter.States': string[];
  'Filter.AttemptStates': string[];
  'Filter.Result': string[];
  'Filter.NotificationTypes': string[];
  'Filter.Types'?: string[];
  readonly 'Filter.FormTemplateIds'?: string[];
  readonly 'Filter.FormBatchIds'?: string[];
  readonly 'Filter.DispensarizationBatchIds'?: string[];
  'Filter.AttemptFrom'?: string;
  'Filter.AttemptTo'?: string;
  'Filter.TextSearch': string;
  SortOrder: string;
}

const buildOutgoingSortQuery = (request: OutgoingCallsRequest) =>
  JSON.stringify({
    sortKeySelector:
      request.SortKeySelector === 'begin'
        ? 'callDateTime'
        : request.SortKeySelector,
    sortOrder: request.SortOrder || 'Asc',
  });

const toTaskQuery = (request: OutgoingCallsRequest): TaskQuery => ({
  from: request['Filter.From'],
  to: request['Filter.To'],
  clinicIds: request['Filter.ClinicIds'],
  states: request['Filter.States'],
  attemptStates: request['Filter.AttemptStates'],
  result: request['Filter.Result'],
  types: request['Filter.Types'] ?? request['Filter.NotificationTypes'],
  textSearch: request['Filter.TextSearch'],
  attemptFrom: request['Filter.AttemptFrom'],
  attemptTo: request['Filter.AttemptTo'],
  formTemplateIds: request['Filter.FormTemplateIds'],
  formBatchIds: request['Filter.FormBatchIds'],
  dispensarizationBatchIds: request['Filter.DispensarizationBatchIds'],
  pageIndex: request.PageIndex,
  pageSize: request.PageSize,
  sortQuery: buildOutgoingSortQuery(request),
});

const initialState: OutgoingCallsState = {
  items: [],
  totalItemsCount: 0,
  status: {
    responseLoading: true,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};

export const getOutgoingCalls = createAsyncThunk(
  'OutgoingCalls/getOutgoingCalls',
  async (request: OutgoingCallsRequest, { rejectWithValue }) => {
    try {
      return await taskApi.getOutboundTasks(toTaskQuery(request));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else
        return rejectWithValue('Не удалось получить список исходящих звонков!');
    }
  },
);

export const getOutboundTaskTypes = createAsyncThunk(
  'OutgoingCalls/getOutboundTaskTypes',
  async (_, { rejectWithValue }) => {
    try {
      return await taskApi.getOutboundTaskTypes();
    } catch (error) {
      return rejectWithValue(
        axios.isAxiosError(error)
          ? error.message
          : 'Не удалось получить типы исходящих заданий!',
      );
    }
  },
);

export const getOutboundTaskReport = createAsyncThunk(
  'OutgoingCalls/getOutboundTaskReport',
  async (request: OutgoingCallsRequest, { rejectWithValue }) => {
    try {
      return await taskApi.getOutboundTaskReport(toTaskQuery(request));
    } catch (error) {
      return rejectWithValue(
        axios.isAxiosError(error)
          ? error.message
          : 'Не удалось получить отчет по исходящим заданиям!',
      );
    }
  },
);

export const getTaskStates = createAsyncThunk(
  'OutgoingCalls/getTaskStates',
  async (_, { rejectWithValue }) => {
    try {
      return await taskApi.getTaskStates();
    } catch (error) {
      return rejectWithValue(
        axios.isAxiosError(error)
          ? error.message
          : 'Не удалось получить статусы заданий!',
      );
    }
  },
);

export const getTaskResults = createAsyncThunk(
  'OutgoingCalls/getTaskResults',
  async (_, { rejectWithValue }) => {
    try {
      return await taskApi.getTaskResults();
    } catch (error) {
      return rejectWithValue(
        axios.isAxiosError(error)
          ? error.message
          : 'Не удалось получить результаты заданий!',
      );
    }
  },
);

export const getTaskSlot = createAsyncThunk(
  'OutgoingCalls/getTaskSlot',
  async (taskId: string, { rejectWithValue }) => {
    try {
      return await taskApi.getTaskSlot(taskId);
    } catch (error) {
      return rejectWithValue(
        axios.isAxiosError(error)
          ? error.message
          : 'Не удалось получить задание по слоту!',
      );
    }
  },
);

export const getTaskDispensarization = createAsyncThunk(
  'OutgoingCalls/getTaskDispensarization',
  async (taskId: string, { rejectWithValue }) => {
    try {
      return await taskApi.getTaskDispensarization(taskId);
    } catch (error) {
      return rejectWithValue(
        axios.isAxiosError(error)
          ? error.message
          : 'Не удалось получить задание диспансеризации!',
      );
    }
  },
);

export const getTaskForm = createAsyncThunk(
  'OutgoingCalls/getTaskForm',
  async (taskId: string, { rejectWithValue }) => {
    try {
      return await taskApi.getTaskForm(taskId);
    } catch (error) {
      return rejectWithValue(
        axios.isAxiosError(error)
          ? error.message
          : 'Не удалось получить задание анкетирования!',
      );
    }
  },
);

export const outgoingCallsSlice = createSlice({
  name: 'outgoingCalls',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getOutgoingCalls.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        return state;
      })
      .addCase(getOutgoingCalls.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        state.items = action.payload.items ?? [];
        state.totalItemsCount = action.payload.totalItemsCount;
        return state;
      })
      .addCase(getOutgoingCalls.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = 'Не удалось загрузить звонки';
        return state;
      });
  },
});

export default outgoingCallsSlice.reducer;

export const selectOutgoingCalls = (state: RootState) =>
  state.outgoingCalls.items;

export const totalOutgoingCalls = (state: RootState) =>
  state.outgoingCalls.totalItemsCount;

export const outgoingCallsStatus = (state: RootState) =>
  state.outgoingCalls.status;
